import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check } from "lucide-react";
import { ArrowDown2 } from "iconsax-reactjs";

export interface DropdownOption {
  label: string;
  value: string;
  icon?: React.ReactNode;
}

interface OtcFilterDropdownProps {
  labelPrefix?: string;
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  icon?: React.ReactNode;
  variant?: "pill" | "header" | "select" | "toolbar";
  className?: string;
  dropdownWidth?: string;
}

export const OtcFilterDropdown: React.FC<OtcFilterDropdownProps> = ({
  labelPrefix,
  value,
  options,
  onChange,
  icon,
  variant = "pill",
  className = "",
  dropdownWidth = "w-48",
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const selectedOption =
    options.find((opt) => opt.value === value) || options[0];
  const activeIcon = icon ?? selectedOption?.icon;

  const handleSelect = (val: string) => {
    onChange(val);
    setIsOpen(false);
  };

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger asChild>
        {variant === "header" ? (
          <button
            type="button"
            className={`inline-flex items-center gap-2 py-0.5 text-[15px] font-[400] tracking-[-0.3px] text-[#383B56] transition-colors hover:text-[#161827] outline-none ${className}`}
          >
            {activeIcon && <span className="text-[16px] leading-none">{activeIcon}</span>}
            {labelPrefix && (
              <span className="aku-body-small-regular text-[#383B56]">
                {labelPrefix}:
              </span>
            )}
            <span className="aku-body-small-medium font-[500] text-[#161827]">
              {selectedOption?.label}
            </span>
            <ArrowDown2
              size={16}
              className={`text-[#737AA8] transition-transform duration-150 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </button>
        ) : variant === "select" ? (
          <button
            type="button"
            className={`flex h-[38px] w-full items-center justify-between rounded-xl border border-[#D0D5DD] bg-white px-3 py-2 text-left text-[13px] text-[#101828] transition-colors hover:border-[#98A2B3] focus:border-[#6F00FF] outline-none ${className}`}
          >
            <span className="truncate">{selectedOption?.label}</span>
            <ArrowDown2
              size={16}
              className={`text-[#667085] transition-transform duration-150 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </button>
        ) : variant === "toolbar" ? (
          <button
            type="button"
            className={`inline-flex h-[38px] items-center gap-1.5 rounded-[8px] bg-[#F8F9FC] px-3.5 text-[13px] font-normal text-[#344054] transition-all hover:bg-[#EEF1FB] hover:text-[#101828] outline-none ${
              isOpen ? "bg-[#EEF1FB] ring-1 ring-[#D0D5DD]" : ""
            } ${className}`}
          >
            {activeIcon && <span className="text-[14px]">{activeIcon}</span>}
            {labelPrefix && (
              <span className="font-normal text-[#667085]">{labelPrefix}:</span>
            )}
            <span className="font-semibold text-[#101828]">
              {selectedOption?.label}
            </span>
            <ArrowDown2
              size={13}
              className={`text-[#667085] transition-transform duration-150 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </button>
        ) : (
          <button
            type="button"
            className={`inline-flex h-[32px] items-center gap-1.5 rounded-full border border-transparent bg-[#F8F9FC] px-3 text-[11px] font-medium text-[#344054] transition-all hover:bg-[#EEF1FB] hover:text-[#101828] outline-none ${
              isOpen ? "bg-[#EEF1FB] ring-1 ring-[#D0D5DD]" : ""
            } ${className}`}
          >
            {activeIcon && <span className="text-[14px]">{activeIcon}</span>}
            {labelPrefix && (
              <span className="font-normal text-[#667085]">{labelPrefix}:</span>
            )}
            <span className="font-medium text-[#101828]">
              {selectedOption?.label}
            </span>
            <ArrowDown2
              size={12}
              className={`text-[#667085] transition-transform duration-150 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </button>
        )}
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          side="bottom"
          sideOffset={6}
          avoidCollisions={true}
          collisionPadding={16}
          className={`z-[120] ${dropdownWidth} overflow-hidden rounded-[14px] border border-[#E1E6FF] bg-white p-1.5 shadow-[0_12px_32px_rgba(15,20,40,0.12)] outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2`}
        >
          <div className="max-h-60 overflow-y-auto py-0.5">
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={`flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left text-[12px] font-medium transition-colors ${
                    isSelected
                      ? "bg-[#F9F5FF] text-[#6941C6]"
                      : "text-[#344054] hover:bg-[#F8F9FC] hover:text-[#101828]"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {option.icon && (
                      <span className="text-sm">{option.icon}</span>
                    )}
                    <span>{option.label}</span>
                  </span>
                  {isSelected && (
                    <Check size={14} className="text-[#6941C6]" />
                  )}
                </button>
              );
            })}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
